import { color } from '@ahamove/design-tokens';
import ATextField, { ClearIcon } from '@ahamove/web-components/lib/ATextField';
import Box from '@ahamove/web-components/lib/Box';
import ButtonBase from '@ahamove/web-components/lib/ButtonBase';
import DialogContent from '@ahamove/web-components/lib/DialogContent';
import Divider from '@ahamove/web-components/lib/Divider';
import InputAdornment from '@ahamove/web-components/lib/InputAdornment';
import List from '@ahamove/web-components/lib/List';
import ListItem, {
  listItemClasses,
} from '@ahamove/web-components/lib/ListItem';
import ListSubheader from '@ahamove/web-components/lib/ListSubheader';
import NavigationBar from '@ahamove/web-components/lib/NavigationBar';
import { outlinedInputClasses } from '@ahamove/web-components/lib/OutlinedInput';
import Paper from '@ahamove/web-components/lib/Paper';
import Tab, { a11yProps } from '@ahamove/web-components/lib/Tab';
import Tabs, { tabsClasses } from '@ahamove/web-components/lib/Tabs';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import React, { Fragment, useEffect, useState } from 'react';
import Modal from 'react-modal';
import { white } from 'tailwindcss/colors';
import { useGetUserProfile } from '@/api/auth/useGetUserProfile';
import type { Address, AutocompleteParams, Feature } from '@/api/place/types';
import { getAutocompleteFn } from '@/api/place/useGetAutocomplete';
import { useGetHubs } from '@/api/place/useGetHubs';
import type { ServiceModalData } from '@/api/service/types';
import { useCurrentLocationStore } from '@/global-stores/useCurrentLocationStore';
import { useLocationStore } from '@/global-stores/useLocationStore';
import { useRentTypeStore } from '@/global-stores/useRentTypeStore';
import { detectPlatform } from '@/lib/common';
import { EV_TRACK_KEY } from '@/lib/constants';
import debounce from '@/lib/debounce';
import { trackEvent } from '@/lib/eventTracking';
import { isServer } from '@/lib/isServer';
import { featureToAddress } from '@/lib/utils';
import { homeConfig } from '@/modules/home/homeConfig';
import CurrentLocation from '@/modules/new/booking/location/CurrentLocation';
import EmptyLocation from '@/modules/new/booking/location/EmptyLocation';
import LocationItem from '@/modules/new/booking/location/LocationItem';
import LocationItemLoading from '@/modules/new/booking/location/LocationItemLoading';
import NoServiceModal from '@/modules/new/booking/location/NoServiceModal';
import PickupAtHub from '@/modules/new/booking/location/PickupAtHub';
import useIsMobile from '@/shared-hooks/useIsMobile';

Modal.setAppElement('#__next');

type Props = {
  open: boolean;
  onClose: () => void;
  onOpen?: () => void;
  loadingServiceModels?: boolean;
  serviceModels: ServiceModalData[] | undefined;
  openNoService: boolean;
  handleCloseNoService: () => void;
};

export default function DialogPickLocation({
  open,
  onClose,
  loadingServiceModels,
  serviceModels,
  openNoService,
  handleCloseNoService,
}: Props) {
  const isMobile = useIsMobile();
  const {
    address: defaultAddressValue,
    setAddress,
    isHub,
    setIsHub,
    city,
  } = useLocationStore();
  const { coords: currentCoords, currentAddress } = useCurrentLocationStore();
  const { rentType } = useRentTypeStore();
  const { data: user } = useGetUserProfile();
  const { t } = useTranslation(homeConfig.i18nNamespaces);
  const [activeTab, setActiveTab] = useState(0);
  const [stepPickHub, setStepPickHub] = useState(0);
  const [cityTitle, setCityTitle] = useState('');
  const [isResetListHub, setIsResetListHub] = useState(false);
  const [addressOptions, setAddressOptions] = useState<readonly Address[]>([]);
  const [addressInputValue, setAddressInputValue] = useState(
    defaultAddressValue?.address
  );
  const [loading, setLoading] = useState(false);
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (!isServer) {
      document.body.style.setProperty(
        'overflow',
        open ? 'hidden' : 'auto',
        'important'
      );
    }
  }, [open]);

  const handleChangeTab = (event: React.SyntheticEvent, tabIndex: number) => {
    if (isHub || (!isHub && !defaultAddressValue)) {
      setAddressInputValue('');
    }
    setActiveTab(tabIndex);
  };

  const { data: listHubs } = useGetHubs(
    {
      lng: currentCoords?.lat as number,
      lat: currentCoords?.lng as number,
    },
    { enabled: false },
    rentType
  );

  const [isNoService, setIsNoService] = useState(false);

  // Update inner value when props value change
  useEffect(() => {
    setAddressInputValue(defaultAddressValue?.address);
    setAddressOptions(defaultAddressValue ? [defaultAddressValue] : []);
  }, [defaultAddressValue, open]);

  const handleInputChange: React.ChangeEventHandler<
    HTMLTextAreaElement | HTMLInputElement
  > = React.useCallback((e) => {
    setAddressInputValue(e.target.value);
  }, []);

  const handleSelectAddress = (addr: Address | null) => {
    if (!addr) return;

    const user_agent = navigator.userAgent;
    const platform = detectPlatform(user_agent);
    trackEvent(EV_TRACK_KEY.ev_location_choose, {
      user_id: user?._id || null,
      userId: user?._id || null,
      user_agent,
      platform,
      type_pickup: isHub ? 'station' : 'delivery',
      city_id: city,
      location: addr.address,
    });

    if (
      addr.address === addressInputValue &&
      addressInputValue === defaultAddressValue?.address
    ) {
      setAddress(addr);
      onClose();
      return;
    }

    setAddressInputValue(addr.address || '');
    setAddress(addr);
  };

  useEffect(() => {
    setIsNoService(!(serviceModels && serviceModels.length > 0));
  }, [serviceModels]);

  useEffect(() => {
    if (serviceModels?.length) {
      onClose();
    }
  }, [serviceModels]);

  const handleClearAddress: React.MouseEventHandler<HTMLButtonElement> = () => {
    // setAddress(null);
    setAddressInputValue('');
    inputRef.current = null;
    // inputRef.current?.focus();
  };

  // Autocomplete predictions fetcher
  const fetch = React.useMemo(
    () =>
      debounce(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (request: AutocompleteParams, callback: (results?: any) => void) => {
          setLoading(true);
          setIsNoService(false);
          if (currentCoords) {
            request.lat = currentCoords.lat;
            request.lng = currentCoords.lng;
          }
          getAutocompleteFn(request)
            .then((res) => {
              setLoading(false);
              callback(res);
            })
            .catch(() => {
              setLoading(false);
              callback({ features: [] });
            })
            .finally(() => setLoading(false));
        },
        800
      ),
    [currentCoords]
  );

  const resetListHub = () => {
    setIsResetListHub(true);
  };

  // const handleCloseNoService = () => {
  //   setOpenNoService(false);
  //   setAddress(null);
  // };

  // Runs on input change
  useEffect(() => {
    // Lock worker
    let active = true;

    // Hide options when input is empty
    // if (addressInputValue === '') {
    //   setAddressOptions((prev) => {
    //     if (
    //       addressValue &&
    //       !prev.find((o) => o.place_id === addressValue.place_id)
    //     ) {
    //       return [addressValue];
    //     }

    //     return [];
    //   });
    //   return undefined;
    // }

    if (addressInputValue === defaultAddressValue?.address) return undefined;

    if (addressInputValue === '' || addressInputValue === undefined)
      return undefined;

    if (addressOptions.findIndex((o) => o.address === addressInputValue) !== -1)
      return undefined;

    if (
      listHubs &&
      listHubs.findIndex((hub) => hub.address === addressInputValue) !== -1
    )
      return undefined;

    if (addressInputValue === currentAddress?.address) return undefined;

    // Fetch autocomplete predictions
    fetch(
      { input: addressInputValue },
      ({ features }: { features: Feature[] }) => {
        if (active) {
          let newOptions: Address[] = [];
          if (features.length > 0) {
            features.forEach((feature: Feature) => {
              const formattedAddress = featureToAddress(feature);
              formattedAddress && newOptions.push(formattedAddress);
            });
          }

          if (defaultAddressValue) {
            const fetchedAddressValueIndex = newOptions.findIndex(
              (o) => o.address === defaultAddressValue.address
            );
            // Include selected address if it is not in the predictions
            if (fetchedAddressValueIndex === -1) {
              // newOptions = [defaultAddressValue, ...newOptions];
              newOptions = [...newOptions];
            } else {
              // Place selected address at the top if it is in the predictions
              newOptions = [
                newOptions[fetchedAddressValueIndex],
                ...newOptions.slice(0, fetchedAddressValueIndex),
                ...newOptions.slice(fetchedAddressValueIndex + 1),
              ];
            }
          }

          setAddressOptions(newOptions);
        }
      }
    );

    return () => {
      // Unlock worker
      active = false;
    };
  }, [defaultAddressValue, addressInputValue, fetch]);

  const handleNextStepPickHub = (value: string) => {
    setCityTitle(value);
    setStepPickHub(1);
    setIsResetListHub(false);
  };

  useEffect(() => {
    if (!isHub || !open) return;
    resetListHub();
    setStepPickHub(0);
  }, [isHub, open]);

  return (
    <>
      <Modal
        isOpen={open}
        onRequestClose={isMobile || loadingServiceModels ? undefined : onClose}
        style={{
          overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.35)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1300,
          },
          content: {
            padding: 0,
            position: 'static',
            inset: 'inherit',
            borderRadius: isMobile ? 0 : 12,
            boxShadow: isMobile ? 'none' : '9px 27px 32px rgba(8, 26, 61, 0.1)',
            overflow: 'hidden',
            overflowY: 'auto',
            pointerEvents: loadingServiceModels ? 'none' : 'auto',
          },
        }}
      >
        {stepPickHub === 0 ? (
          <NavigationBar
            title={t('booking:booking_box.pickup_end_return').toString()}
            onClose={
              loadingServiceModels
                ? undefined
                : () => {
                    onClose();
                  }
            }
            closePosition={isMobile ? 'left' : 'right'}
            sx={
              isMobile
                ? {
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 50,
                  }
                : null
            }
          />
        ) : (
          <NavigationBar
            title={t('booking:location.hub_at') + cityTitle}
            twoLines={!isMobile}
            onBack={() => {
              setStepPickHub(0);
              resetListHub();
            }}
            sx={
              isMobile
                ? {
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 50,
                  }
                : {
                    maxWidth: 375,
                    '&.MuiToolbar-root': {
                      '.MuiTypography-root': { ml: '0 !important', mr: -2 },
                    },
                  }
            }
          />
        )}

        {stepPickHub === 0 ? (
          <Box
            data-test-id="pickHubHeader"
            px={2}
            sx={
              isMobile
                ? {
                    position: 'fixed',
                    right: 0,
                    left: 0,
                    top: 48,
                    zIndex: 50,
                    backgroundColor: white,
                  }
                : undefined
            }
          >
            <Tabs
              variant="fullWidth"
              sx={{
                [`.${tabsClasses.indicator}`]: {
                  zIndex: 2,
                },

                // [`.${tabClasses.root}`]: {
                //   width: 1 / 2,
                // },

                '&:after': {
                  content: '""',
                  width: 'calc(100% - 32px)',
                  height: 2,
                  bgcolor: 'divider',
                  position: 'absolute',
                  bottom: 0,
                  left: 16,
                  right: 16,
                },
              }}
              value={activeTab}
              onChange={handleChangeTab}
            >
              <Tab
                index={0}
                icon={
                  <Image
                    src="/static/images/point.png"
                    alt="/static/images/point.png"
                    width={16}
                    height={16}
                  />
                }
                iconPosition="start"
                label={t('booking:location.deliver_to_you')}
                {...a11yProps(0)}
                sx={{ whiteSpace: 'nowrap' }}
              />
              <Tab
                index={1}
                icon={
                  <Image
                    src="/static/images/hub.png"
                    alt="/static/images/hub.png"
                    width={16}
                    height={16}
                  />
                }
                iconPosition="start"
                label={t('booking:location.pickup_at_hub')}
                {...a11yProps(1)}
                sx={{ whiteSpace: 'nowrap' }}
              />
            </Tabs>
          </Box>
        ) : null}
        {!isMobile && stepPickHub !== 1 ? <Divider variant="middle" /> : null}
        <Box
          sx={
            isMobile
              ? {
                  position: 'fixed',
                  right: 0,
                  left: 0,
                  top: '96px',
                  zIndex: 50,
                  display: activeTab === 0 ? 'block' : 'none',
                }
              : { display: activeTab === 0 ? 'block' : 'none' }
          }
        >
          <List
            sx={
              isMobile
                ? {
                    position: 'fixed',
                    right: 0,
                    left: 0,
                    top: '96px',
                    zIndex: 50,
                    backgroundColor: white,
                  }
                : undefined
            }
          >
            <ListItem
              sx={{
                py: 1.5,
                // mb: loading ? 0 : 0.5,
              }}
            >
              <ATextField
                autoComplete="off"
                inputRef={inputRef}
                value={addressInputValue}
                placeholder={String(t('booking:location.enter_location'))}
                onChange={handleInputChange}
                fullWidth
                data-test-id="inputSearchAddress"
                sx={{
                  [`.${outlinedInputClasses.root}`]: {
                    borderRadius: 3,
                    backgroundColor: 'rgba(230, 233, 236, 0.6)',
                  },
                  [`.${outlinedInputClasses.input}`]: {
                    color: addressInputValue
                      ? `${color.neutral[90]} !important`
                      : `${color.neutral[60]} !important`,
                    fontWeight: '700 !important',
                  },
                  [`.${outlinedInputClasses.focused}`]: {
                    backgroundColor: white,
                  },
                }}
                InputProps={{
                  autoFocus: true,
                  ...(addressInputValue || !!inputRef.current?.value
                    ? {
                        endAdornment: (
                          <InputAdornment position="end">
                            <ButtonBase
                              disableRipple
                              onClick={handleClearAddress}
                              data-test-id="clearAddressBtn"
                            >
                              <ClearIcon />
                            </ButtonBase>
                          </InputAdornment>
                        ),
                      }
                    : {}),
                }}
              />
            </ListItem>
            {/* {loading ? <LinearProgress /> : null} */}
            <Divider />
          </List>
        </Box>
        <DialogContent
          sx={{
            p: 0,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: isMobile ? '100%' : 375,
              maxWidth: isMobile ? '100%' : 375,
            }}
          >
            <Box
              data-test-id="searchAddressResultContainer"
              sx={{ display: activeTab === 0 ? 'block' : 'none' }}
            >
              <List
                sx={
                  isMobile
                    ? {
                        maxHeight: 'calc(100vh - 176px)',
                        height: 'calc(100% - 176px)',
                        overflowX: 'hidden',
                        overflowY: 'auto',
                        position: 'fixed',
                        right: 0,
                        left: 0,
                        top: 176,
                        // pb: 3,
                        bgcolor: 'grey.50',

                        [`.${listItemClasses.root}`]: {
                          bgcolor: 'common.white',
                        },
                      }
                    : {
                        maxHeight: 367,
                        height: 367,
                        bgcolor: 'grey.50',
                        overflowX: 'hidden',
                        overflowY: 'auto',
                      }
                }
              >
                <CurrentLocation
                  onSelect={(address: Address) => {
                    handleSelectAddress(address);
                    setIsHub(false);
                  }}
                />
                {loading ? (
                  Array.from(Array(isMobile ? 3 : 2).keys()).map((item) => {
                    return <LocationItemLoading key={item} />;
                  })
                ) : addressOptions && addressOptions.length > 0 ? (
                  (
                    addressOptions.filter((a) =>
                      isNoService
                        ? a.address !== defaultAddressValue?.address
                        : true
                    ) || []
                  ).map((option, idx) => (
                    <LocationItem
                      key={idx}
                      address={option}
                      loading={
                        loadingServiceModels &&
                        addressInputValue === option.address
                      }
                      onClick={() => {
                        handleSelectAddress(option);
                        setIsHub(false);
                      }}
                      sx={
                        idx < addressOptions.length - 1
                          ? {
                              '&::after': {
                                content: '""',
                                width: 'calc(100% - 56px)',
                                height: '1px',
                                bgcolor: 'grey.100',
                                position: 'absolute',
                                bottom: 0,
                                right: 0,
                              },
                            }
                          : {}
                      }
                    />
                  ))
                ) : addressInputValue ? (
                  <EmptyLocation
                    type="location"
                    innerProps={{ pt: 3, pb: 1.5 }}
                  />
                ) : (
                  <Fragment />
                )}
                {/* {isNoService && defaultAddressValue && !!addressInputValue ? (
                  <EmptyLocation
                    type="service"
                    innerProps={{ pt: 3, pb: 1.5 }}
                  />
                ) : null} */}
              </List>
            </Box>
            <Box
              data-test-id="pickAtHubContainer"
              sx={{
                display: activeTab === 1 ? 'block' : 'none',
                ...(isMobile
                  ? {
                      position: 'fixed',
                      right: 0,
                      left: 0,
                      top: stepPickHub === 0 ? 96 : 48,
                      zIndex: 50,
                      bgcolor: 'white',
                      height: '100%',
                    }
                  : {}),
              }}
            >
              <List
                sx={{
                  bgcolor: 'white',
                  maxHeight: isMobile
                    ? stepPickHub === 0
                      ? 'calc(100% - 96px)'
                      : 'calc(100% - 48px)'
                    : stepPickHub === 0
                    ? 448
                    : 496,
                  height: isMobile
                    ? stepPickHub === 0
                      ? 'calc(100% - 96px)'
                      : 'calc(100% - 48px)'
                    : stepPickHub === 0
                    ? 448
                    : 496,
                  overflowX: 'hidden',
                  overflowY: 'auto',
                }}
              >
                <ListSubheader
                  sx={(theme) => ({
                    p: 2,
                    ...theme.typography.h5,
                    color: 'grey.900',
                  })}
                >
                  {(!defaultAddressValue && !currentAddress) ||
                  stepPickHub === 0
                    ? t('booking:location.select_your_city')
                    : t('booking:location.select_hub_near_you')}
                </ListSubheader>
                <Divider variant="inset" sx={{ ml: 0 }} />
                <PickupAtHub
                  onSelectHub={(addr) => {
                    // if (!currentAddress && !defaultAddressValue) {
                    setIsHub(true);
                    // }
                    handleSelectAddress(addr);
                  }}
                  handleNextStepPickHub={handleNextStepPickHub}
                  isResetListHub={isResetListHub}
                />
              </List>
            </Box>
          </Paper>
        </DialogContent>
      </Modal>

      <NoServiceModal
        address={addressInputValue}
        open={openNoService}
        onClose={handleCloseNoService}
      />
    </>
  );
}
